import AppButton from 'features/common/AppButton';
import AppPopUp from 'features/common/AppPopUp';
import AppSelectbox from 'features/common/AppSelectbox';
import { Label } from 'features/common/Styles';
import { head } from 'lodash';
import { memo, useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { updateArticle } from 'redux/article';
import { getEntities, getEntity } from 'redux/entity';
import styled from 'styled-components';
import { ArticleContext } from 'utils/contexts/ArticleContext';
import { useEnterKeydown, useEscapeKeydown } from 'utils/hooks/useKeydown';
import { useSelect } from 'utils/hooks/useSelect';
import { AppInput } from 'features/common/AppInput';

export const SaveArticleModal = memo<{ onSaveComplete: () => void }>(({ onSaveComplete }) => {
  const ref = useRef<HTMLInputElement>(null);

  const dispatch = useDispatch();
  const { t } = useTranslation(['home', 'common']);

  const { modalType, setModalType, articleId } = useContext(ArticleContext);
  const article = useSelect(({ entity }) => getEntity(entity, 'article', articleId));
  const folders = useSelect(({ entity }) =>
    getEntities(entity, 'folder').filter((folder) => !folder.is_draft && !!folder.name)
  );

  const currentFolder = folders.find((folder) => folder.id === article?.folder_id) ?? head(folders);

  const [folderId, setFolderId] = useState<string | null>(currentFolder?.id || null);
  const [title, setTitle] = useState(article?.title ?? '');

  // @effects

  useEscapeKeydown(() => {
    if (modalType === 'saveDraft') setModalType(null);
  }, [modalType]);

  useEnterKeydown(() => {
    if (modalType === 'saveDraft') handleMoveClick();
  }, [currentFolder, article, folderId, modalType]);

  // @handlers

  const handleMoveClick = useCallback(() => {
    if (!title) return toast.info(t('notEmptyProjectName'));
    if (!folderId) return toast.info(t('notEmptyFolderName'));
    if (article) dispatch(updateArticle(article.id, { folder_id: folderId, title }));
    setModalType(null);
    onSaveComplete();
  }, [article, articleId, folderId, title]);

  const handleChangeFolder = useCallback((newValues) => {
    setFolderId(newValues[0].value);
  }, []);

  const handleSkipClick = useCallback(() => {
    setModalType(null);
    onSaveComplete();
  }, []);

  // @render

  return (
    <AppPopUp open={modalType === 'saveDraft'} onClose={() => setModalType(null)}>
      <Container>
        <Title>{`📄 ${t('saveProject')}`}</Title>
        <Info>{t('moveProjectInfo')}</Info>
        <ProjectNameInput ref={ref} placeholder={t('projectName')} value={title} onChange={setTitle} />

        <SelectBox
          options={folders.map(({ id, name }) => ({ label: name, value: id }))}
          defaultValue={currentFolder ? { label: currentFolder.name, value: currentFolder.id } : undefined}
          placeholder={t('folder')}
          onChange={handleChangeFolder}
        />
        <SaveButton text={t('save')} onClick={handleMoveClick} />

        <Label
          onClick={handleSkipClick}
          style={{ opacity: 0.4, cursor: 'pointer', fontSize: 13, textDecorationLine: 'underline', marginTop: 12 }}
        >
          {t('skipThisForNow')}
        </Label>
      </Container>
    </AppPopUp>
  );
});

const Info = styled.span`
  font-family: Nunito Sans;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: left;
  margin-top: 0px;
  width: 360px;
  margin-bottom: 16px;
  color: ${(props) => props.theme.colors.dark[70]};
`;

const ProjectNameInput = styled(AppInput)`
  min-width: 380px !important;
  width: 380px !important;
`;

const Container = styled.div`
  padding: 20px;
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
`;

const Title = styled(Label)`
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0em;
  text-align: left;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const SaveButton = styled(AppButton)`
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0em;
  text-align: center;
  margin-top: 20px;
  background: ${({ theme }) => theme.colors.dark[100]};
  border-radius: 4px;
`;

const SelectBox = styled(AppSelectbox)`
  & > div {
    border: 1px solid ${({ theme }) => theme.colors.gray[60]};
    border-radius: 4px;
    margin-top: 10px;
    padding: 10px 4px;

    & > div {
      background: transparent !important;
      border: none !important;

      & > :not(:nth-child(1), :nth-child(2)) {
        border-radius: 4px;
        border: rgba(255, 255, 255, 0.08) 1px solid;
        padding: 4px 0px;
      }

      & > div > span {
        color: ${({ theme }) => theme.colors.black};
        border: none;
        font-family: Nunito Sans;
        font-size: 14px;
        font-weight: 600;
        line-height: 16px;
        letter-spacing: 0em;
        text-align: left;
        padding: 12px 16px;
        width: 100%;
      }

      & > div > p {
        color: ${({ theme }) => theme.colors.dark[100]} !important;
        font-family: Nunito Sans;
        font-size: 14px;
        font-weight: 700;
      }

      & > div > div > * {
        color: ${({ theme }) => theme.colors.dark[70]} !important;
      }
    }
  }
`;
